import { useContext, useEffect, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useHistory, useLocation } from "react-router-dom";
import { CourseStructureCtx, InsightsCtx, UserCtx } from "../../interfaces";
import { UserContext } from "../../context/UserContext";
import { UserDropdown } from "../v2/UserDropdown";
import { Role, _AuthType } from "../../enums";
import {
  AppBar,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatDate, generateGtmEvent, HandleDataLayer } from "../../Helpers";
import { AlertBellIcon } from "./notification/AlertBellIcon";
import { KeyboardArrowUpRounded } from "@mui/icons-material";
import { CourseStructureContext } from "../../context/CourseStructureContext";
import { InsightsContext } from "../../context/InsightsContext";
import { MacsAuthenticator } from "../../hooks/useMacsAuthenticator";
import { ReactComponent as CanvasIcon } from "../../assets/canvas-icon.svg";
import useStorageState from "../../hooks/useStorageState";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: 72,
    boxSizing: "border-box",
    paddingLeft: 32,
    paddingRight: 32,
    maxWidth: "1280px",
    width: "100%",
    // [theme.breakpoints.up("lg")]: {
    //   paddingLeft: 86,
    //   paddingRight: 86,
    // },
    [theme.breakpoints.down("md")]: {
      paddingLeft: 12,
      paddingRight: 12,
    },
  },
  logo: {
    color: theme.palette.text.primary,
    fontSize: 24,
    fontWeight: 700,
    letterSpacing: 0.18,
    marginRight: 48,
  },
  tabs: {
    "& .MuiTab-root": {
      color: theme.palette.text.primary,
      opacity: 1,
      fontSize: 18,
    },
  },
  tab: {
    height: 70,
  },
  dropdown: {
    marginTop: "10px",
    backgroundColor: "transparent",
    "& .MuiMenu-paper": {
      boxShadow: "0px 8px 12px #00000019",
      borderRadius: "8px",
      "& .MuiList-root": {
        backgroundColor: "white",
      },
    },
  },
  mainContentLink: {
    paddingLeft: 16,
    paddingRight: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: 0,
    height: "100%",
    textDecoration: "none",
    "&:focus": {
      opacity: 1,
      background: "white",
      outlineColor: "#1A237E",
    },
  },
  mainContentLinkText: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: "1.125em",
  },
  arrowDown: {
    height: "fit-content",
    transform: "rotate(0deg)",
    transition: "transform .2s linear",
  },
  arrowUp: {
    height: "fit-content",
    transform: "rotate(180deg)",
    transition: "transform .2s linear",
  },

  pulseButton: {
    animation: `$pulseAnimation 2s ease 2`,
  },

  "@keyframes pulseAnimation": {
    "0%": {
      boxShadow: "0 0 0 0 #0092E0",
      animationTimingFunction: "ease-in-out",
    },
    "100%": {
      boxShadow: "0 0 0 16px rgba(236, 178, 46, 0)",
      animationTimingFunction: "linear",
    },
  },
}));

interface Page {
  id: number;
  htmlId?: string;
  label: string;
  path?: string;
  activePaths: string[];
  restrictTo?: Role[];
  dropdownOptions?: DropdownOption[];
  dropdownEmptymessage?: string;
  role?: string;
}

interface DropdownOption {
  path: string;
  label: string;
}

const pages: Page[] = [
  {
    id: 0,
    htmlId: "nav-home-button",
    label: "Home",
    role: "link",
    path: "/class-overview",
    activePaths: ["/class-overview", "/learning-overview"],
  },
  {
    id: 2,
    htmlId: "nav-your-classes-button",
    label: "Your Classes",
    role: "tab",
    activePaths: ["/class"],
    restrictTo: [Role.teacher],
  },
  {
    id: 3,
    htmlId: "nav-explore-button",
    label: "Explore",
    role: "link",
    path: "/explore",
    activePaths: [
      "/explore",
      "/explore/browse",
      "/explore/saved",
      "/explore/search",
      "/explore/module",
      "/explore/activities",
    ],
    restrictTo: [Role.teacher, Role.student, Role.admin, Role.super],
  },
  {
    id: 4,
    htmlId: "nav-management-button",
    label: "Management",
    role: "tab",
    path: "/user-management",
    activePaths: [
      "/user-management",
      "/school-management",
      "/class-management",
      "/archived-user",
      "/course-management",
      // "/archived-class-management", // commented out per QA request
    ],
    restrictTo: [Role.admin, Role.super],
  },
  // {
  //   id: 5,
  //   label: "Insights",
  //   path: "/insights",
  //   activePaths: ["/insights"],
  //   restrictTo: [Role.teacher, Role.admin, Role.super],
  // },
];

const ALL_AVAILABLE_ROLES: Role[] = [Role.teacher, Role.admin, Role.super, Role.student];

export const TopAppBar = () => {
  const classes = useStyles();
  const userCtx = useContext(UserContext) as UserCtx;
  const insightsCtx = useContext(InsightsContext) as InsightsCtx;
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  const [userInfo, setUserInfo] = useState({
    initials: "",
    firstName: "",
    formattedName: "",
    email: "",
    accessibility: false,
    role: Role.student,
  });
  const history = useHistory();
  const location = useLocation();
  const [isFocused, setIsFocused] = useState(false);
  const [seenCanvasButton, setSeenCanvasButton] = useStorageState({
    storageKey: "lxp-seencanvasbutton",
  });
  const [emphasizeCanvasButton, setEmphasizeCanvasButton] = useState(false);

  const courseMetadata =
    courseStructureCtx.courseTreeStructure?.getCurrentCourseMetadata();
  // change home to class-overview if teacher
  if (userCtx.dbUser?.role === Role.teacher) {
    // pages[0].path = "/class-overview";
    // pages[0].activePaths = ["/class-overview"];
    const dropdownOptions: DropdownOption[] =
      insightsCtx.classesActive?.map((item) => {
        return {
          path: insightsCtx.getClassUrl(item.id),
          label:
            item.settings?.nickname ??
            `${item.course_name} ${item.period_block} ${
              item.start_date ? formatDate(new Date(item.start_date)) : ""
            }`,
        };
      }) ?? [];

    const archivedDropdownOptions: DropdownOption[] =
      insightsCtx.classesArchived?.map((item) => {
        return {
          path: insightsCtx.getClassUrl(item.id),
          label: `${item.course_name} ${item.period_block} ${
            item.start_date ? formatDate(new Date(item.start_date)) : ""
          }`,
        };
      }) ?? [];

    pages[1].dropdownOptions = [...dropdownOptions, ...archivedDropdownOptions];
    pages[1].dropdownEmptymessage = "You have not been assigned to any classes yet";
    // const dropdownObserverOptions: DropdownOption[] =
    //   classDetails.active.observingClasses.map((item) => {
    //     return {
    //       path: `/class/${item.id}/insights`,
    //       label: `${item.subject} ${item.period_block}`,
    //     };
    //   });
    // pages[1].dropdownOptions = [...dropdownOptions, ...dropdownObserverOptions];
  }

  if (
    userCtx.dbUser.role === Role.teacher ||
    userCtx.dbUser.role === Role.admin ||
    userCtx.dbUser.role === Role.super
  ) {
    const allCourses = courseStructureCtx.allCourses;
    if (allCourses) {
      const dropdownOptions: DropdownOption[] = allCourses.map((item) => {
        return {
          path: `/explore/activities?courseId=${item.id}`,
          label: `${item.name}`,
        };
      });
      dropdownOptions.push({
        path: `/explore`,
        label: `See All Courses`,
      });
      pages[2].dropdownOptions = [...dropdownOptions];
      pages[2].dropdownEmptymessage = "No course available yet";
    }
  }

  // change home to learning-overview if student
  if (userCtx.dbUser?.role === Role.student) {
    pages[0].path = "/learning-overview";
  }

  if (userCtx.dbUser?.role === Role.student) {
    if (userCtx.studentCurrentClass)
      pages[2].path = `/explore/browse?courseId=${userCtx.studentCurrentClass?.course?.id}`;
    else pages[2].path = `/explore`;
  }

  if (courseMetadata?.top_app_bar_buttons?.hide_home_for_role) {
    pages[0].restrictTo = ALL_AVAILABLE_ROLES.filter(
      (role) => !courseMetadata?.top_app_bar_buttons?.hide_home_for_role?.includes(role)
    );
  } else {
    pages[0].restrictTo = [Role.teacher, Role.admin, Role.super, Role.student];
  }

  if (
    courseMetadata?.top_app_bar_buttons?.hide_insights_for_role &&
    MacsAuthenticator.authHook.authType === _AuthType.LTI
  ) {
    pages[1].restrictTo = ALL_AVAILABLE_ROLES.filter(
      (role) =>
        !courseMetadata?.top_app_bar_buttons?.hide_insights_for_role?.includes(role)
    );
  } else {
    pages[1].restrictTo = [Role.teacher];
  }

  if (courseMetadata?.top_app_bar_buttons?.hide_explore_for_role) {
    pages[2].restrictTo = ALL_AVAILABLE_ROLES.filter(
      (role) =>
        !courseMetadata?.top_app_bar_buttons?.hide_explore_for_role?.includes(role)
    );
  } else {
    pages[2].restrictTo = [Role.teacher, Role.admin, Role.super, Role.student];
  }

  if (courseMetadata?.top_app_bar_buttons?.hide_management_for_role) {
    pages[3].restrictTo = ALL_AVAILABLE_ROLES.filter(
      (role) =>
        !courseMetadata?.top_app_bar_buttons?.hide_management_for_role?.includes(role)
    );
  } else {
    pages[3].restrictTo = [Role.admin, Role.super];
  }

  if (courseMetadata?.top_app_bar_buttons?.home_button_override_route) {
    if (
      userCtx.dbUser?.role === Role.student &&
      MacsAuthenticator.authHook.authType === _AuthType.LTI
    ) {
      pages[0].path = courseMetadata?.top_app_bar_buttons?.home_button_override_route;
    }
  } else {
    if (userCtx.dbUser?.role === Role.student) {
      pages[0].path = "/learning-overview";
    } else {
      pages[0].path = "/class-overview";
    }
  }

  const activePages = [...pages].filter(
    (page) => !page.restrictTo || page.restrictTo.includes(userCtx.dbUser.role)
  );

  useEffect(() => {
    if (!userCtx || !userCtx.dbUser) {
      return;
    }

    let dbUser = userCtx.dbUser;
    let firstName = dbUser.first_name;

    if (firstName.length > 1) {
      firstName = firstName.toLocaleLowerCase();
      firstName = firstName[0].toLocaleUpperCase() + firstName.slice(1);
    } else {
      firstName = firstName.toLocaleUpperCase();
    }

    let lastName = dbUser.last_name;

    if (lastName.length > 1) {
      lastName = lastName.toLocaleLowerCase();
      lastName = lastName[0].toLocaleUpperCase() + lastName.slice(1);
    } else {
      lastName = lastName.toLocaleUpperCase();
    }

    const fullName = firstName + " " + lastName;
    const newUserInfo = {
      initials: (dbUser.first_name[0] + dbUser.last_name[0]).toUpperCase(),
      firstName: dbUser.first_name,
      formattedName: fullName,
      email: dbUser.email,
      accessibility: dbUser.accessibility,
      role: dbUser.role as Role,
    };
    setUserInfo(newUserInfo);
  }, [userCtx]);

  const handleNavClick = (path: string | undefined) => {
    if (path) {
      history.push(path);
    } else {
    }
  };

  const getCurrentTab = () => {
    const currentPath = location.pathname;
    const currentPage = activePages.find(
      (page) =>
        (page.path && currentPath.startsWith(page.path)) ||
        (page.activePaths &&
          page.activePaths.some((path) => currentPath.startsWith(path)))
    );
    return currentPage ? activePages.indexOf(currentPage) : false; // return false to deselect tab
  };

  const HandleFocus = () => {
    setIsFocused(true);
  };

  const showCanvasButton =
    MacsAuthenticator.authHook.authType === _AuthType.LTI &&
    MacsAuthenticator.authHook.ltiReturnUrl &&
    courseMetadata?.use_digit_study_tool === true;

  // if the canvas button has not been seen, emphasize it and then remove emphasis after 2 animations
  useEffect(() => {
    if (!seenCanvasButton && showCanvasButton) {
      setEmphasizeCanvasButton(true);
      const timer = setTimeout(() => {
        setEmphasizeCanvasButton(false);
        setSeenCanvasButton("true");
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [seenCanvasButton, setSeenCanvasButton, showCanvasButton]);

  return (
    <AppBar
      sx={{
        alignItems: "center",
        borderBottom: `2px solid #dedede`,
        boxSizing: "border-box",
      }}
      position="static"
      id="top-app-bar">
      <Toolbar disableGutters className={classes.root} tabIndex={-1}>
        {/* <AdbIcon sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }} /> */}
        <Typography
          variant="h4"
          component="h1"
          noWrap
          sx={{
            display: { xs: "none", sm: "flex" },
          }}
          className={classes.logo}>
          Digit
        </Typography>
        <a
          tabIndex={0}
          onFocus={HandleFocus}
          href="#main"
          className={classes.mainContentLink}
          style={{
            opacity: isFocused ? 1 : 0,
            top: isFocused ? "1.5rem" : "-10rem",
            position: isFocused ? "static" : "absolute",
          }}>
          <Typography
            variant="h4"
            component={"p"}
            className={classes.mainContentLinkText}>
            Skip to main content
          </Typography>
        </a>

        {activePages.length > 0 && (
          <Box sx={{ flexGrow: 1 }}>
            <Tabs
              className={classes.tabs}
              value={getCurrentTab()}
              role="navigation"
              aria-label="Navigation">
              {activePages.map((page) => (
                <NavItem
                  key={page.label}
                  onClick={() => {
                    handleNavClick(page.path);
                  }}
                  handleNavClick={handleNavClick}
                  page={page}
                />
              ))}
            </Tabs>
          </Box>
        )}
        {showCanvasButton && (
          <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}>
            <Tooltip
              open={emphasizeCanvasButton}
              title={<span>New! You can now return to Canvas from here.</span>}
              placement="bottom-end"
              arrow
              PopperProps={{
                sx: {
                  [`& .MuiTooltip-tooltip`]: {
                    maxWidth: "none",
                  },
                  "& .MuiTooltip-arrow": {
                    marginLeft: "76px",
                  },
                },
              }}>
              <Button
                type="button"
                sx={{ height: 52 }}
                variant="outlined"
                color="primary"
                className={emphasizeCanvasButton ? classes.pulseButton : undefined}
                onClick={() => {
                  generateGtmEvent({
                    event: "link",
                    action: "click",
                    name: "onclick",
                    type: "internal link",
                    region: "navbar",
                    section: "main navbar",
                    text: "back to canvas",
                  });
                  const returnUrl = MacsAuthenticator.authHook.ltiReturnUrl;
                  if (returnUrl) window.location.href = returnUrl;
                }}>
                <CanvasIcon
                  style={{ width: "20px", height: "20px", marginRight: "8px" }}
                />
                Back to Canvas
              </Button>
            </Tooltip>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            alignItems: "center",
            marginLeft: "auto",
          }}>
          {userInfo.role === Role.teacher && (
            <AlertBellIcon
              isNotificationEnabled={userCtx.dbUser.notifications_enabled}
              notifications={userCtx.dbUser.user_notifications}
            />
          )}
          <UserDropdown userInfo={userInfo} />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

interface NavItemProps {
  page: Page;
  onClick: () => void;
  handleNavClick: (path: string) => void;
}

const NavItem = (props: NavItemProps) => {
  const { onClick, page, handleNavClick } = props;
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Tab
        label={
          <Stack direction="row" alignItems="center">
            {page.label}
            {page.dropdownOptions && (
              <KeyboardArrowUpRounded
                sx={{ marginLeft: "8px" }}
                className={open ? classes.arrowDown : classes.arrowUp}
              />
            )}
          </Stack>
        }
        sx={page.dropdownOptions ? { paddingRight: "4px" } : {}}
        id={page.htmlId}
        role={page.role}
        onClick={(e) => {
          if (!page.dropdownOptions) {
            HandleDataLayer(
              "link",
              "click",
              "internal link",
              "navbar",
              "main navbar",
              page.label,
              "onclick"
            );
            onClick();
          } else {
            HandleDataLayer(
              "collapse",
              "open",
              "click",
              "navbar",
              "main navbar",
              page.label,
              "onclick"
            );
            handleClick(e);
          }
        }}
        className={classes.tab}
        tabIndex={0}
      />
      {page.dropdownOptions && (
        <Menu
          className={classes.dropdown}
          id={`${page.htmlId}-dropdown`}
          anchorEl={anchorEl}
          // anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          // transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={open}
          onClose={() => {
            HandleDataLayer(
              "collapse",
              "close",
              "click",
              "navbar",
              "main navbar",
              page.label,
              "onclick"
            );
            handleClose();
          }}
          MenuListProps={{
            "aria-labelledby": "",
          }}>
          {page.dropdownOptions.map((option, index) => {
            return (
              <MenuItem
                key={`${index}-${page.htmlId}-dropdown-${option.label}`}
                onClick={() => {
                  generateGtmEvent({
                    name: "onclick",
                    event: "link",
                    action: "click",
                    type: "internal link",
                    region: "navbar",
                    section: "main navbar",
                    text: `${option.label}`.toLowerCase(),
                  });
                  handleNavClick(option.path);
                  handleClose();
                }}>
                {option.label}
              </MenuItem>
            );
          })}
          {page.dropdownOptions.length < 1 && (
            <MenuItem key={`${page.htmlId}-dropdown-empty`} onClick={handleClose}>
              {page.dropdownEmptymessage}
            </MenuItem>
          )}
        </Menu>
      )}
    </>
  );
};
